import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

// 连接状态常量
export const CONN_STATE = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
};

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connState, setConnState] = useState(CONN_STATE.DISCONNECTED);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [userIdConfirmed, setUserIdConfirmed] = useState(false); // 新增：服务端确认 userId
  const pendingQueue = useRef([]);
  const socketRef = useRef(null);
  const { user } = useAuth();

  const flushQueue = useCallback(() => {
    const q = pendingQueue.current;
    if (q.length === 0 || !socketRef.current?.connected) return;
    console.log(`📤 重连后发送 ${q.length} 条待发消息`);
    pendingQueue.current = [];
    q.forEach((evt) => {
      socketRef.current.emit(evt.event, evt.data);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setSocket(null);
      setConnState(CONN_STATE.DISCONNECTED);
      setUserIdConfirmed(false);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const newSocket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    socketRef.current = newSocket;

    const onConnect = () => {
      console.log('🔗 Socket 已连接');
      setConnState(CONN_STATE.CONNECTED);
      setReconnectAttempt(0);
      setUserIdConfirmed(false);
      newSocket.emit('user_join', {
        userId: user.id,
        username: user.username,
      });
      // 重连后刷掉待发队列
      flushQueue();
    };

    const onReconnect = () => {
      console.log('✅ Socket 重连成功');
      setConnState(CONN_STATE.CONNECTED);
      setReconnectAttempt(0);
      setUserIdConfirmed(false);
      // 【修复】重连后重新发送 user_join，否则服务端 socket.userId 为空
      if (user) {
        newSocket.emit('user_join', { userId: user.id, username: user.username });
      }
      // 重连后刷掉待发队列
      flushQueue();
    };

    newSocket.on('connect', onConnect);
    newSocket.on('reconnect', onReconnect);

    newSocket.on('disconnect', (reason) => {
      console.log(`🔌 Socket 断开: ${reason}`);
      setConnState(reason === 'io client disconnect' ? CONN_STATE.DISCONNECTED : CONN_STATE.RECONNECTING);
      setUserIdConfirmed(false);
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      console.log(`🔄 重连中 (第 ${attempt} 次)`);
      setConnState(CONN_STATE.RECONNECTING);
      setReconnectAttempt(attempt);
    });

    newSocket.on('reconnect_error', (err) => {
      console.error('❌ 重连失败:', err.message);
    });

    newSocket.on('connect_error', (err) => {
      console.error('连接错误:', err.message);
    });

    // 【修复】服务端确认 userId 已注册
    newSocket.on('user_joined', (data) => {
      console.log('✅ userId 已确认:', data.userId);
      setUserIdConfirmed(true);
      // 收到确认后刷一次待发队列
      flushQueue();
    });

    setSocket(newSocket);

    return () => {
      newSocket.removeAllListeners();
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnState(CONN_STATE.DISCONNECTED);
      setUserIdConfirmed(false);
    };
  }, [user, flushQueue]);

  // 暴露一个安全 emit 方法：在线+身份确认后再发，未确认则进队列等
  const safeEmit = useCallback((event, data) => {
    if (socketRef.current?.connected && userIdConfirmed) {
      socketRef.current.emit(event, data);
      return true;
    }
    pendingQueue.current.push({ event, data });
    console.log(`📥 离线/未确认身份，消息已入队 (队列: ${pendingQueue.current.length})`);
    return false;
  }, [userIdConfirmed]);

  const value = { socket, connState, reconnectAttempt, userIdConfirmed, safeEmit, pendingCount: pendingQueue.current.length };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
